import { useRef, useState, useEffect } from 'react';
import CardItemView from '@/components/CardItemView';
import leftIcon from '@/assets/chevron_left_white.svg';
import rightIcon from '@/assets/chevron_right_white.svg';
import { RecommendationCardsSkeleton } from '@/components/Skeleton';

const RecommendationCardsSection = ({ cards, title, isLoading = false }) => {
  const containerRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // One row per page, 4 or 5 cards depending on screen width
  const [isMaxScreen, setIsMaxScreen] = useState(window.innerWidth >= 1440);
  const [cardsPerPage, setCardsPerPage] = useState(isMaxScreen ? 5 : 4); 
  const [isHovering, setIsHovering] = useState(false);
  const [shouldAnimateTransition, setShouldAnimateTransition] = useState(false);

  // Dynamically adjust cards per page (1 row, 4 or 5 cards) based on screen width
  useEffect(() => {
    const handleResize = () => {
      const maxScreen = window.innerWidth >= 1440;
      setIsMaxScreen(maxScreen);
      setCardsPerPage(maxScreen ? 5 : 4);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const storageKey = `recommendation_cards_${title}`;

  // Calculate total pages based on cards length and cardsPerPage
  useEffect(() => {
    setShouldAnimateTransition(false);
    const pages = Math.ceil(cards.length / cardsPerPage);
    setTotalPages(pages);

    if (pages === 0) {
      setCurrentPage(0);
      return;
    }

    // Restore saved page if available
    const savedPage = sessionStorage.getItem(storageKey);
    if (savedPage !== null) {
      const parsed = parseInt(savedPage, 10);
      if (!Number.isNaN(parsed)) {
        setCurrentPage(Math.max(0, Math.min(parsed, pages - 1)));
        return;
      }
    }

    // Reset to first page when cardsPerPage changes or no saved page
    setCurrentPage(0);

    const enableTimeout = requestAnimationFrame(() => {
      setShouldAnimateTransition(true);
    });

    return () => cancelAnimationFrame(enableTimeout);
  }, [cards, cardsPerPage, storageKey]);

  // Persist current page
  useEffect(() => {
    if (totalPages > 0) {
      sessionStorage.setItem(storageKey, currentPage.toString());
    }
  }, [currentPage, totalPages, storageKey]);

  // Split cards into chunks for pagination
  const getPageCards = (pageIndex) => {
    const start = pageIndex * cardsPerPage;
    return cards.slice(start, start + cardsPerPage);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Generate pages to render (current, prev, next)
  const renderPages = () => {
    // Always render current page
    let pagesToRender = [currentPage];
    
    // Add previous page if it exists
    if (currentPage > 0) {
      pagesToRender.unshift(currentPage - 1);
    }
    
    // Add next page if it exists
    if (currentPage < totalPages - 1) {
      pagesToRender.push(currentPage + 1);
    }
    
    return pagesToRender;
  };

  if (isLoading) {
    return <RecommendationCardsSkeleton />;
  }

  return (
    <div className="mb-8">
      <h2 className="text-2xl font-bold text-[#111111] mb-6">{title}</h2>

      <div 
        ref={containerRef}
        className="relative overflow-hidden" 
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {/* Carousel pages */}
        <div className="relative w-full">
          {renderPages().map((pageIndex) => (
            <div 
              key={pageIndex}
              className={`w-full ${shouldAnimateTransition ? 'transition-transform duration-500 ease-in-out' : ''} ${
                Math.abs(pageIndex - currentPage) > 1 ? 'hidden' : 'block'
              }`}
              style={{ 
                transform: `translateX(${(pageIndex - currentPage) * 100}%)`,
                position: pageIndex === currentPage ? 'relative' : 'absolute',
                top: 0,
                left: 0,
                right: 0,
                width: '100%',
                zIndex: pageIndex === currentPage ? 1 : 0
              }}
            >
              <div className="grid grid-cols-4 max:grid-cols-5 gap-4 grid-rows-1">
                {getPageCards(pageIndex).map((card) => (
                  <CardItemView
                    key={card.id}
                    id={card.id}
                    name={card.localizedRawName}
                    type={card.type === 'SINGLE_CARD' ? 'PSA_SINGLE_CARD' : 'CARD_BOX'}
                    image={card.images?.[0]?.url}
                    brand={card.brand?.localizedName}
                    price={(() => {
                      const vals = [
                        card.stats?.lowestPriceOnSale,
                        card.stats?.lowestPriceOnSaleOfficial,
                      ].filter(v => typeof v === 'number' && v > 0);
                      return vals.length ? Math.min(...vals) : 0;
                    })()}
                    orders={card.stats?.salesVolume}
                    code={card.code}
                    rawCode={card.rawCode}
                    set={card.set}
                    rarityInfo={card.rarityInfo}
                    className="w-full"
                    trackEventName="card_click"
                  />

                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Navigation buttons - only visible on hover */}
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 0}
          className={`absolute left-2 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-[#111111] transition-all duration-300 hover:scale-110 z-10 ${isHovering && currentPage > 0 ? 'opacity-100' : 'opacity-0'}`}
          aria-label="Previous page"
        >
          <img src={leftIcon} alt="Previous page" className="w-8 h-8" />
        </button>
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages - 1}
          className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-[#111111] transition-all duration-300 hover:scale-110 z-10 ${isHovering && currentPage < totalPages - 1 ? 'opacity-100' : 'opacity-0'}`}
          aria-label="Next page"
        >
          <img src={rightIcon} alt="Next page" className="w-8 h-8" />
        </button>
      </div>
    </div>
  );
};

export default RecommendationCardsSection; 